// Mixin for pages that want to show spending plans in the sidebar
// Requires: Services mixin, SpendingAnalysis mixin, SpendingPlans mixin, registerSidebar injection

export default {
	inject: ['registerSidebar'],

	data() {
		return {
			spendingPlansSidebarItems: [],
			selectedPlanId: null
		}
	},

	methods: {
		fetchAndRegisterSpendingPlans(selectedSlotKey = null) {
			this._getSpendingPlansFull(() => {
				this._extractActivePlans();
				this.buildSpendingPlansSidebarItems(selectedSlotKey);
				this.registerSpendingPlansSidebar();
			});
		},

		buildSpendingPlansSidebarItems(selectedSlotKey = null) {
			const items = [];
			const plans = this.spendingPlans?.plans || [];
			const activePlans = plans.filter(p => p.plan?.amount > 0);

			if (!activePlans.length || !this.spendingAnalysisStructured?.grandparentAnalysis) {
				this.spendingPlansSidebarItems = items;
				return;
			}

			// Get spending data for the selected slot
			const slotAnalysis = this.spendingAnalysisStructured.grandparentAnalysis
				.filter(a => a.slotName === selectedSlotKey);

			// Collect all grouped analysis items
			const allGroups = [];
			slotAnalysis.forEach(a => {
				if (a.groupedAnalysis) {
					a.groupedAnalysis.forEach(g => allGroups.push(g));
				}
			});

			// Build sidebar items
			activePlans.forEach(plan => {
				const classId = plan.classification?.id;
				const budget = plan.plan?.amount || 0;
				if (!budget) return;

				// Find matching spending from analysis
				const group = allGroups.find(g => String(g.transactionClassification?.id) === String(classId));
				const spent = group ? Math.abs(group.amountNet || 0) : 0;
				const percent = Math.min(100, Math.round((spent / budget) * 100));

				items.push({
					id: `plan-${classId}`,
					title: plan.classification?.fullName || 'Plan',
					subtitle: `${this.formatter.format(spent)} / ${this.formatter.format(budget)}`,
					progress: percent,
					colour: percent >= 100 ? '#c62828' : percent >= 80 ? '#f9a825' : '#2e7d32',
					// Store extra data for click handler
					_planData: {
						classificationId: classId,
						name: plan.classification?.fullName,
						iconPath: plan.classification?.iconPath,
						budget: budget,
						spent: spent,
						remaining: budget - spent,
						percent: percent
					}
				});
			});

			this.spendingPlansSidebarItems = items;
		},

		registerSpendingPlansSidebar() {
			if (!this.registerSidebar) return;

			this.registerSidebar({
				config: {
					actionButton: null,
					pageItemsTitle: 'Spending Plans',
					pageItemsEmptyText: 'No plans set',
					pageItems: this.spendingPlansSidebarItems,
					currentPageItemId: this.selectedPlanId
				},
				data: this,
				handlers: {
					onActionClick: () => {},
					onPageItemClick: (id) => { this.handleSpendingPlanClick(id); },
					onPageItemDelete: () => {},
					onPageItemRename: () => {},
					onOpenSettings: () => { if (this.showSettings !== undefined) this.showSettings = true; }
				}
			});
		},

		handleSpendingPlanClick(planId) {
			if (!String(planId).startsWith('plan-')) return;

			const item = this.spendingPlansSidebarItems.find(i => i.id === planId);
			if (!item) return;

			this.selectedPlanId = planId;

			// Call the page's handler if it exists
			if (typeof this.onSpendingPlanSelected === 'function') {
				this.onSpendingPlanSelected(item._planData);
			}

			this.registerSpendingPlansSidebar();
		},

		clearSpendingPlanSelection() {
			this.selectedPlanId = null;
			this.registerSpendingPlansSidebar();
		},

		getSpendingPlanData(planId) {
			const item = this.spendingPlansSidebarItems.find(i => i.id === planId);
			return item?._planData || null;
		}
	}
}
